<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { VButton, VIcon } from '@/modules/shared/components'
import TheLayout from '@/modules/shared/layouts/the-layout.vue'
import IndividualHeader from '@/modules/investing/components/individual-header.vue'
import IndividualNav from '@/modules/investing/components/individual-nav.vue'
import IndividualAccountLayout from '@/modules/investing/components/individuals/individual-account-layout.vue'
import { useSkeleton } from '@/modules/core/composables/useSkeleton'
import { useInvestingInvestorStore } from '@/modules/investing/stores/investor-store'
import { scrollToTop } from '@/modules/investing/utils/scroll-to-section'
import CandidateForm from '@/modules/connecting/components/candidate-form.vue'
import { useCandidateStore } from '@/modules/connecting/stores/candidate-store'
import { useUnivesrityStore } from '@/modules/shared/stores/university-store'

const route = useRoute()
const { individual_id, slug } = route.params as { individual_id: string; slug: string }

const candidateStore = useCandidateStore()
const investorStore = useInvestingInvestorStore()
const currentIndividual = ref(null)
const investmentPreferencesForm = ref(null)
const universityStore = useUnivesrityStore()
const inFormMode = ref(false)
const candidate = computed(() => candidateStore.getCandidate(currentIndividual.value?.connecting_candidate_id))

const isNewCandidate = computed(() => !currentIndividual.value.connecting_candidate_id)

const { skeleton, hideSkeleton, showSkeleton } = useSkeleton()

const fetch = async () => {
  await investorStore.fetchIndividual(route.params.individual_id)
  currentIndividual.value = investorStore.items.get(`individual:${route.params.individual_id}`)

  if (!currentIndividual.value.connecting_candidate_id) return
  await candidateStore.fetchCandidate(currentIndividual.value.connecting_candidate_id)
}

const editForm = () => {
  inFormMode.value = true
}

const cancelEdit = () => {
  investmentPreferencesForm.value.resetForm()
  inFormMode.value = false
  scrollToTop()
}

const submit = async (payload) => {
  if (isNewCandidate.value) {
    payload = {
      ...payload,
      subject_id: individual_id,
      subject_type: 'Investor',
    }
    await candidateStore.createCandidate(payload)
  } else {
    await candidateStore.updateCandidate(payload)
  }
  await fetch()
  cancelEdit()
}

onMounted(async () => {
  showSkeleton()
  await Promise.all([universityStore.fetchUSUniversities(), fetch()])
  hideSkeleton()
})
</script>

<template>
  <TheLayout>
    <IndividualHeader />
    <IndividualNav />

    <IndividualAccountLayout selectedTab="investment-preferences">
      <CandidateForm
        ref="investmentPreferencesForm"
        :universities="universityStore.us_universities"
        :candidate="candidate"
        :inDisplayMode="!inFormMode"
        :onSubmit="submit"
        :skeleton="skeleton"
      >
        <template #actions>
          <VButton variant="v-white" :click="editForm" v-if="!inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Edit
              <VIcon name="Pencil" />
            </div>
          </VButton>
          <VButton variant="v-white" :click="cancelEdit" v-if="inFormMode">
            <div class="flex items-center gap-2 stroke-[#3b88af] text-sm">
              Cancel
              <VIcon name="X" />
            </div>
          </VButton>
        </template>
      </CandidateForm>
    </IndividualAccountLayout>
  </TheLayout>
</template>
